import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import UserMiddleware from 'features/auth/components/UserMiddleware/UserMiddleware';
import Main from 'features/main/Main';
import { useEffect } from 'react';
import { CookiesProvider } from 'react-cookie';
import { isMobile } from 'react-device-detect';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import FullscreenProgress from 'shared/components/FullscreenProgress/FullscreenProgress';
import PageContainer from 'shared/components/PageContainer/PageContainer';
import ScrollToTop from 'shared/components/ScrollToTop/ScrollToTop';
import { ConfirmDialogProvider } from 'shared/contexts/ConfirmDialogContext/ConfirmDialogContext';
import { SnackBarProvider } from 'shared/contexts/SnackbarContext/SnackbarContext';
import { WebInAppProvider } from 'shared/contexts/WebInAppContext/WebInAppContext';
import { persistor, store } from 'shared/redux/store';
import { loadJS } from 'shared/utils/common';
import { initGTM } from 'shared/utils/gtm';
import 'styles/global.css';
import 'swiper/css';
import 'swiper/css/grid';
import theme from 'theme';

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    initGTM();

    if (process.env.REACT_APP_ENABLE_ERUDA === 'true' && isMobile) {
      const src = `//cdn.jsdelivr.net/npm/eruda`;
      loadJS(src).then(() => {
        if ((window as any).eruda && (window as any).eruda._isInit) return;
        (window as any).eruda.init();
      });
    }
    return () => {
      (window as any).eruda &&
        (window as any).eruda._isInit &&
        (window as any).eruda.destroy();
    };
  }, []);

  return (
    <CookiesProvider>
      <Provider store={store}>
        <PersistGate loading={<FullscreenProgress />} persistor={persistor}>
          <HelmetProvider>
            <ThemeProvider theme={theme}>
              <ConfirmDialogProvider>
                <SnackBarProvider>
                  <BrowserRouter>
                    <Helmet
                      titleTemplate={`%s - ${process.env.REACT_APP_NAME}`}
                      defaultTitle={`${process.env.REACT_APP_NAME}`}
                      htmlAttributes={{ lang: i18n.language }}
                    >
                      <meta name='description' content='' />
                    </Helmet>
                    <CssBaseline />
                    <WebInAppProvider>
                      <PageContainer>
                        <UserMiddleware>
                          <ScrollToTop />
                          <Main />
                        </UserMiddleware>
                      </PageContainer>
                    </WebInAppProvider>
                  </BrowserRouter>
                </SnackBarProvider>
              </ConfirmDialogProvider>
            </ThemeProvider>
          </HelmetProvider>
        </PersistGate>
      </Provider>
    </CookiesProvider>
  );
}

export default App;
